import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Box,
  Chip,
  SxProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect, useState } from "react";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import { getMessageRecipientsById } from "../api/requests/dashboard";

interface Recipient {
  userName: string;
  rank?: string | null;
}

interface LevelData {
  levelRecipientReport: {
    forwardedLevel: number;
    forwadedRecipients: Recipient[];
    seenRecipients: Recipient[];
    unseenRecipients: Recipient[];
  }[];
  totalRecipientsCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: {
    messageId: string;
    recipientsCount: number;
  };
}

interface IconCountItem {
  Icon: React.ElementType;
  count: number;
  color: string;
}

interface IconCountListProps {
  forwadedRecipients: Recipient[];
  seenRecipients: Recipient[];
  unseenRecipients: Recipient[];
  fontSize?: number;
}

const IconCountList: React.FC<IconCountListProps> = ({
  forwadedRecipients = [],
  seenRecipients = [],
  unseenRecipients = [],
  fontSize = 15,
}) => {
  const iconCountData: IconCountItem[] = [
    {
      Icon: ShareOutlined,
      count: forwadedRecipients.length,
      color: "#16a34a",
    },
    {
      Icon: VisibilityOutlinedIcon,
      count: seenRecipients.length,
      color: "#2563eb",
    },
    {
      Icon: VisibilityOffOutlinedIcon,
      count: unseenRecipients.length,
      color: "#9ca3af",
    },
  ];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {iconCountData.map(({ Icon, count, color }, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          <Icon sx={{ fontSize, color }} />
          <Typography sx={{ fontSize: fontSize === 15 ? "14px" : "12px" }}>
            {count}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

interface RecipientCategory {
  title: string;
  Icon: React.ElementType;
  color: string;
  recipients: Recipient[];
  backgroundColor: string;
  showRank?: boolean;
}

interface RecipientListProps {
  forwardedLevel: number;
  categories: RecipientCategory[];
}

const RecipientList: React.FC<RecipientListProps> = ({
  forwardedLevel,
  categories,
}) => {
  return (
    <>
      {categories.map(
        ({ title, Icon, color, recipients, backgroundColor, showRank }) => (
          <Box key={`${title}-${forwardedLevel}`} sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Icon sx={{ color }} />
              <Typography fontWeight={600}>
                {title} ({recipients.length})
              </Typography>
            </Box>
            {recipients.map((user, idx) => (
              <Paper
                key={`${title.toLowerCase()}-${forwardedLevel}-${idx}`}
                sx={{
                  p: 1,
                  mb: 1,
                  backgroundColor,
                  boxShadow: "none",
                }}
              >
                <Box sx={userCardStyles}>
                  <Typography sx={userIconStyles}>
                    <AccountCircleOutlined sx={{ fontSize: "14px" }} />{" "}
                    {user.userName}
                  </Typography>
                  <Box sx={rankLabelStyles}>
                    <Typography sx={{ color: "black", fontSize: "12px" }}>
                      {showRank && user.rank ? ` ${user.rank}` : "--"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )
      )}
    </>
  );
};

interface LevelBarProps {
  forwardedLevel: number;
  width?: string;
}

const LevelBar: React.FC<LevelBarProps> = ({
  forwardedLevel,
  width = "1%",
}) => {
  const getBarColor = (level: number) => {
    switch (level) {
      case 1:
        return "#a855f7";
      case 2:
        return "#3b82f6";
      default:
        return "#22c55e";
    }
  };

  return (
    <Box
      sx={{
        width,
        backgroundColor: getBarColor(forwardedLevel),
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 1,
        borderRadius: width === "4px" ? "2px" : undefined,
      }}
    />
  );
};

const MessageRecipientsPopup: React.FC<Props> = ({ open, onClose, data }) => {
  const [tabValue, setTabValue] = useState<number>(0);
  const [recipientData, setRecipientData] = useState<LevelData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getMessageRecipientsById(Number(data.messageId));
      const normalizedData = Array.isArray(res.data) ? res.data : [res.data];
      setRecipientData(normalizedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      setRecipientData([]);
    }
  };

  useEffect(() => {
    if (!open) setTabValue(0);
    if (open) fetchData();
  }, [data.messageId, open]);

  const getLevelTitle = (level: number) => {
    return level === 1 ? "Direct Recipients" : `Level ${level}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "#e9e9e9",
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: "20px" }}>
            Message Recipients
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Total:{" "}
            {Array.isArray(recipientData) &&
              recipientData.reduce(
                (sum, item) => sum + item.totalRecipientsCount,
                0
              )}{" "}
            Recipients
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "14px", color: "#938282" }}>
          Recipients grouped by sharing level
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={tabValue} onChange={handleTabChange} sx={tabStyles}>
          <Tab label="List View" />
          <Tab label="Summary" />
        </Tabs>

        {/* List View */}
        {tabValue === 0 && (
          <>
            {loading ? (
              <Box sx={noMessageCardStyles}>
                <Typography>Loading...</Typography>
              </Box>
            ) : recipientData.every(
                (item) => item.levelRecipientReport.length === 0
              ) ? (
              <Paper>
                <Box sx={noMessageCardStyles}>
                  <Typography>No Messages</Typography>
                </Box>
              </Paper>
            ) : (
              Array.isArray(recipientData) &&
              recipientData?.length > 0 &&
              recipientData.map(({ levelRecipientReport }, i) =>
                levelRecipientReport.map(
                  (
                    {
                      forwardedLevel,
                      forwadedRecipients,
                      seenRecipients,
                      unseenRecipients,
                    },
                    j
                  ) => {
                    const recipientCategories: RecipientCategory[] = [
                      {
                        title: "Shared",
                        Icon: ShareOutlined,
                        color: "#16a34a",
                        recipients: forwadedRecipients || [],
                        backgroundColor: "#dcfce7",
                        showRank: true,
                      },
                      {
                        title: "Seen",
                        Icon: VisibilityOutlinedIcon,
                        color: "#2563eb",
                        recipients: seenRecipients || [],
                        backgroundColor: "#dbeafe",
                        showRank: true,
                      },
                      {
                        title: "Unseen",
                        Icon: VisibilityOffOutlinedIcon,
                        color: "#9ca3af",
                        recipients: unseenRecipients || [],
                        backgroundColor: "#f3f4f6",
                        showRank: true,
                      },
                    ];

                    return (
                      <Box
                        sx={{ position: "relative", mb: 2 }}
                        key={`${i}-${j}`}
                      >
                        <LevelBar forwardedLevel={forwardedLevel} />
                        <Accordion sx={accordionStyles}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ background: "white" }}
                          >
                            <Box sx={levelStyles}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <GroupOutlinedIcon sx={{ color: "#49514d" }} />
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {`Level ${forwardedLevel}`}
                                  </Typography>
                                  <IconCountList
                                    forwadedRecipients={
                                      forwadedRecipients || []
                                    }
                                    seenRecipients={seenRecipients || []}
                                    unseenRecipients={unseenRecipients || []}
                                  />
                                </Box>
                              </Box>
                              <Chip
                                label={forwadedRecipients?.length || 0}
                                size="small"
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pb: 2 }}>
                            <RecipientList
                              forwardedLevel={forwardedLevel}
                              categories={recipientCategories}
                            />
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    );
                  }
                )
              )
            )}
          </>
        )}

        {/* Summary View */}
        {tabValue === 1 && (
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography gutterBottom fontWeight="bold">
                Recipients by level
              </Typography>
              {recipientData.every(
                (item) => item.levelRecipientReport.length === 0
              ) ? (
                <Box
                  sx={{ display: "flex", justifyContent: "center", padding: 5 }}
                >
                  No Recipient Messages
                </Box>
              ) : (
                Array.isArray(recipientData) &&
                recipientData.map((item, index) =>
                  item.levelRecipientReport.map(
                    (
                      {
                        forwardedLevel,
                        forwadedRecipients,
                        seenRecipients,
                        unseenRecipients,
                      },
                      reportIndex
                    ) => (
                      <Box
                        key={`${index}-${reportIndex}`}
                        sx={summaryCardDetailsStyles}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Box
                            sx={{ display: "flex", alignItems: "flex-start" }}
                          >
                            <LevelBar
                              forwardedLevel={forwardedLevel}
                              width="4px"
                            />
                            <Box ml={1.5}>
                              <Typography fontWeight="bold" fontSize={14}>
                                {getLevelTitle(forwardedLevel)}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "10px", color: "#938282" }}
                              >
                                {forwadedRecipients?.length || 0} Recipients
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <IconCountList
                          forwadedRecipients={forwadedRecipients || []}
                          seenRecipients={seenRecipients || []}
                          unseenRecipients={unseenRecipients || []}
                          fontSize={12}
                        />
                      </Box>
                    )
                  )
                )
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageRecipientsPopup;

const userIconStyles: SxProps = {
  fontSize: "14px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 0.8,
};

const tabStyles: SxProps = {
  mb: 2,
  borderRadius: 1,
  backgroundColor: "#f1f5f9",
  minHeight: "40px",
  borderBottom: "none",
  display: "flex",
  padding: 1,
  justifyContent: "space-between",
  "& .MuiTab-root": {
    width: "50%",
    textTransform: "none",
    minHeight: "40px",
    fontWeight: 500,
    borderRadius: "8px 8px 0 0",
  },
  "& .Mui-selected": {
    backgroundColor: "#ededed",
    color: "#000000",
    fontWeight: 600,
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
};

const accordionStyles: SxProps = {
  borderRadius: 2,
  overflow: "hidden",
  boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
  "&:before": { display: "none" },
  pl: "1%",
  position: "relative",
  zIndex: 2,
  background: "none",
};

const levelStyles: SxProps = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const userCardStyles: SxProps = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
};

const rankLabelStyles: SxProps = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  padding: "2px 6px",
  borderRadius: "4px",
};

const noMessageCardStyles: SxProps = {
  background: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "50px",
};

const summaryCardDetailsStyles: SxProps = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  mb: 2,
  position: "relative",
};
